import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import { getImageUrl } from '../../utils/api';
import TemplateEditorWorkspace, { TEMPLATE_EDITOR_EXTENSIONS } from './TemplateEditorWorkspace';
import PlaceholdersSidebar from './PlaceholdersSidebar';
import EditableModalTitle from './EditableModalTitle';
import ErrorModal from './ErrorModal';
import EditorOptionsPanel from './EditorOptionsPanel';
import {
  DEFAULT_TEMPLATE_FONT_SIZE,
  wrapTemplateContent,
  parseTemplateContent
} from '../../utils/templateEditorFontSize';
import './Salon.css';

const CreateContractTemplateModal = ({ isOpen, onClose, salonId, onTemplateCreated, editingContract }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    title: '',
    contract_type: 'rental',
    is_active: true
  });
  const [loading, setLoading] = useState(false);

  // Lock body scroll when modal is open
  useScrollLock(isOpen);
  const [error, setError] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [originalFontSize, setOriginalFontSize] = useState(DEFAULT_TEMPLATE_FONT_SIZE);
  const [fontSize, setFontSize] = useState(DEFAULT_TEMPLATE_FONT_SIZE);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const originalContentRef = useRef('');
  const originalFontSizeRef = useRef(DEFAULT_TEMPLATE_FONT_SIZE);
  const fontSizeRef = useRef(DEFAULT_TEMPLATE_FONT_SIZE);

  useEffect(() => {
    originalContentRef.current = originalContent;
  }, [originalContent]);

  useEffect(() => {
    originalFontSizeRef.current = originalFontSize;
  }, [originalFontSize]);

  useEffect(() => {
    fontSizeRef.current = fontSize;
  }, [fontSize]);

  const editor = useEditor({
    // Avoid accessing the view before EditorContent mounts (Firefox throws on editor.view)
    immediatelyRender: false,
    // Since TipTap 2.7/@tiptap/react v3, useEditor() no longer re-renders the component on every
    // transaction by default (a perf change) - without this, every isActive()/getAttributes()
    // check in the toolbar (bold, headings, alignment, image overlay, all of it) reads stale
    // state and never visibly updates as the cursor/selection moves.
    shouldRerenderOnTransaction: true,
    extensions: TEMPLATE_EDITOR_EXTENSIONS,
    content: '',
    // Without this, ProseMirror's HTML parser collapses runs of spaces down to one
    // (both on initial load and while typing) - this preserves them as typed.
    parseOptions: { preserveWhitespace: 'full' },
    editorProps: {
      attributes: {
        class: 'contract-editor-content',
      },
    },
    onUpdate: ({ editor }) => {
      // Check if content has changed
      const currentContent = editor.getHTML();
      setHasUnsavedChanges(
        currentContent !== originalContentRef.current
        || fontSizeRef.current !== originalFontSizeRef.current
      );

      // Check if editor is empty (check for actual text content)
      const textContent = editor.getText().trim();
      const isEmpty = textContent === '';
      setIsEditorEmpty(isEmpty);
    },
  });

  // Fetch available placeholders
  const fetchPlaceholders = useCallback(async () => {
    try {
      setLoadingPlaceholders(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/contracts/placeholders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaceholders(response.data.placeholders || []);
    } catch (error) {
      console.error('Error fetching placeholders:', error);
    } finally {
      setLoadingPlaceholders(false);
    }
  }, [salonId]);

  // Load placeholders when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPlaceholders();
    }
  }, [isOpen, fetchPlaceholders]);

  // Check initial editor state
  useEffect(() => {
    if (editor) {
      const textContent = editor.getText().trim();
      setIsEditorEmpty(textContent === '');
    }
  }, [editor]);

  // Populate form when editing
  useEffect(() => {
    if (!isOpen || !editor || editor.isDestroyed) return;

    if (editingContract) {
      setFormData({
        title: editingContract.title || '',
        contract_type: editingContract.contract_type || 'rental',
        is_active: editingContract.is_active !== undefined ? editingContract.is_active : true
      });

      // Set editor content
      const { fontSize: parsedFontSize, html } = parseTemplateContent(
        editingContract.contract_content || ''
      );
      editor.commands.setContent(html, { parseOptions: { preserveWhitespace: 'full' } });
      setFontSize(parsedFontSize);
      setOriginalFontSize(parsedFontSize);
      setOriginalContent(html);
      setHasUnsavedChanges(false);

      // Update empty state
      const textContent = editor.getText().trim();
      setIsEditorEmpty(textContent === '');
    } else {
      // Reset form for new contract
      setFormData({
        title: '',
        contract_type: 'rental',
        is_active: true
      });
      // Reset editor content to empty
      editor.commands.setContent('');
      setOriginalContent('');
      setFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
      setOriginalFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
      setHasUnsavedChanges(false);

      // Update empty state
      setIsEditorEmpty(true);
    }
  }, [isOpen, editingContract, editor]);

  const handleFontSizeChange = (next) => {
    setFontSize(next);
    setHasUnsavedChanges(
      (editor?.getHTML() || '') !== originalContent || next !== originalFontSize
    );
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setHasUnsavedChanges(true);
  };

  const handleActiveChange = (e) => {
    setFormData(prev => ({ ...prev, is_active: e.target.checked }));
    setHasUnsavedChanges(true);
  };

  const handleTitleChange = (title) => {
    setFormData(prev => ({ ...prev, title }));
    setHasUnsavedChanges(true);
  };

  const insertPlaceholder = (placeholder) => {
    if (editor) {
      editor.commands.insertContent(`{{${placeholder}}}`);
      editor.commands.focus();
    }
  };

  const uploadImage = async (file) => {
    const token = localStorage.getItem('token');
    const formDataPayload = new FormData();
    formDataPayload.append('image', file);
    const response = await axios.post(`/api/salons/${salonId}/contracts/upload-image`, formDataPayload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    return getImageUrl(response.data.url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError(t('settings.titleRequired'));
      return;
    }

    if (!editor.getHTML() || editor.getHTML() === '<p></p>') {
      setError(t('settings.contentRequired'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const contractData = {
        title: formData.title,
        contract_content: wrapTemplateContent(editor.getHTML(), fontSize),
        contract_type: formData.contract_type,
        is_active: formData.is_active
      };

      let response;
      if (editingContract) {
        // Update existing contract
        response = await axios.put(`/api/salons/${salonId}/contracts/${editingContract.id}`, contractData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new contract
        response = await axios.post(`/api/salons/${salonId}/contracts`, contractData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.status === 200 || response.status === 201) {
        // Update original content to current content after successful save
        const currentContent = editor.getHTML();
        setOriginalContent(currentContent);
        setOriginalFontSize(fontSize);
        setHasUnsavedChanges(false);
        onTemplateCreated();

        // Close modal for new contracts or when called from unsaved changes dialog
        // Keep open for editing existing contracts (regular update)
        if (!editingContract || showUnsavedWarning) {
          handleClose();
        }
      }
    } catch (error) {
      console.error('Error saving contract template:', error);
      setError(error.response?.data?.error || `Failed to ${editingContract ? 'update' : 'create'} contract template`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      return;
    }

    // Reset form state
    setFormData({
      title: '',
      contract_type: 'rental',
      is_active: true
    });
    setError('');
    setHasUnsavedChanges(false);
    setShowUnsavedWarning(false);
    if (editor) {
      editor.commands.setContent('');
    }
    setFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
    setOriginalFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
    onClose();
  };

  const handleDiscardChanges = () => {
    setFormData({
      title: '',
      contract_type: 'rental',
      is_active: true
    });
    setError('');
    setHasUnsavedChanges(false);
    setShowUnsavedWarning(false);
    if (editor) {
      editor.commands.setContent('');
    }
    setFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
    setOriginalFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
    onClose();
  };

  const handleSaveAndClose = async () => {
    // Validate required fields first
    if (!formData.title.trim()) {
      setError(t('settings.titleRequired'));
      return;
    }

    if (!editor.getHTML() || editor.getHTML() === '<p></p>') {
      setError(t('settings.contentRequired'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const contractData = {
        title: formData.title,
        contract_content: wrapTemplateContent(editor.getHTML(), fontSize),
        contract_type: formData.contract_type,
        is_active: formData.is_active
      };

      let response;
      if (editingContract) {
        // Update existing contract
        response = await axios.put(`/api/salons/${salonId}/contracts/${editingContract.id}`, contractData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new contract
        response = await axios.post(`/api/salons/${salonId}/contracts`, contractData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.status === 200 || response.status === 201) {
        // Update original content to current content after successful save
        const currentContent = editor.getHTML();
        setOriginalContent(currentContent);
        setOriginalFontSize(fontSize);
        setHasUnsavedChanges(false);
        setShowUnsavedWarning(false);
        onTemplateCreated();

        // Reset form state and close
        setFormData({
          title: '',
          contract_type: 'rental',
          is_active: true
        });
        setError('');
        if (editor) {
          editor.commands.setContent('');
        }
        onClose();
      }
    } catch (error) {
      console.error('Error saving contract template:', error);
      setError(error.response?.data?.error || `Failed to ${editingContract ? 'update' : 'create'} contract template`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelWarning = () => {
    setShowUnsavedWarning(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal contract-template-modal">
        <div className="modal-header">
          <EditableModalTitle
            value={formData.title}
            onChange={handleTitleChange}
            placeholder={t('settings.enterTemplateTitle')}
            editLabel={t('settings.editTitle')}
          />
          <div className="modal-header-right">
            <label className="checkbox-label modal-header-active-toggle">
              <input type="checkbox" checked={formData.is_active} onChange={handleActiveChange} />
              <span className="checkmark"></span>
              {t('settings.active')}
            </label>
            <button className="modal-close" onClick={handleClose}>
              ×
            </button>
          </div>
        </div>

        <div className="contract-modal-content">
          {/* Placeholders Sidebar */}
          <PlaceholdersSidebar description={t('settings.clickToInsert')}>
            {loadingPlaceholders ? (
              <div className="loading-spinner-small"></div>
            ) : placeholders.length === 0 ? (
              <div className="no-placeholders">
                <p>{t('settings.noPlaceholdersAvailable')}</p>
              </div>
            ) : (
              <div className="placeholders-list">
                {placeholders.map((placeholder, index) => {
                  const translatedDescription = t(`settings.placeholders.${placeholder.key}`, placeholder.description);
                  return (
                    <button
                      key={index}
                      type="button"
                      className="placeholder-button"
                      onClick={() => insertPlaceholder(placeholder.key)}
                      title={translatedDescription}
                    >
                      <span className="placeholder-key">{`{{${placeholder.key}}}`}</span>
                      <span className="placeholder-description">{translatedDescription}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </PlaceholdersSidebar>

          {/* Main Form Content */}
          <div className="contract-form-content">
            <form onSubmit={handleSubmit} className="base-form">
              <EditorOptionsPanel>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="contract_type">{t('settings.contractType')}</label>
                    <select
                      id="contract_type"
                      name="contract_type"
                      value={formData.contract_type}
                      onChange={handleInputChange}
                    >
                      <option value="rental">{t('settings.rental')}</option>
                      <option value="sale">{t('settings.sale')}</option>
                    </select>
                  </div>
                </div>
              </EditorOptionsPanel>

              <div className="form-row editor-row">
                <div className="form-group">
                  <TemplateEditorWorkspace
                    editor={editor}
                    fontSize={fontSize}
                    onFontSizeChange={handleFontSizeChange}
                    isEditorEmpty={isEditorEmpty}
                    onUploadImage={uploadImage}
                    onUploadError={setError}
                  />
                </div>
              </div>

            </form>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="salon-button secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="salon-button primary"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? (editingContract ? t('settings.updating') : t('settings.creating')) : (editingContract ? t('settings.updateTemplate') : t('settings.createTemplate'))}
          </button>
        </div>
      </div>

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedWarning && (
        <div className="modal-overlay" style={{ zIndex: 1001 }}>
          <div className="modal-content" style={{
            maxWidth: '450px',
            width: '90%',
            margin: '0 auto'
          }}>
            <div className="modal-header" style={{
              padding: '20px 24px 16px 24px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                margin: '0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#374151'
              }}>{t('settings.unsavedChanges')}</h3>
            </div>
            <div className="modal-body" style={{
              padding: '20px 24px',
              lineHeight: '1.5'
            }}>
              <p style={{
                margin: '0',
                fontSize: '14px',
                color: '#6b7280'
              }}>{t('settings.unsavedChangesMessage')}</p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px 20px 24px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                className="salon-button secondary"
                onClick={handleCancelWarning}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
              >
                {t('settings.continueEditing')}
              </button>
              <button
                type="button"
                className="salon-button danger"
                onClick={handleDiscardChanges}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
              >
                {t('settings.discardChanges')}
              </button>
              <button
                type="button"
                className="salon-button primary"
                onClick={handleSaveAndClose}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
              >
                {loading ? t('settings.saving') : t('settings.saveAndClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorModal message={error} onClose={() => setError('')} />
    </div>
  );
};

export default CreateContractTemplateModal;
